import { useMemo } from "react";
import type { ComponentProps } from "./index";
import { useWorkspaceJson } from "./useWorkspaceJson";
import { layoutTree, measureText, parseTree, type Leaf, type StageBox, type TreeView } from "./treeLayout.ts";
import { canvasMeasurer, useElementWidth } from "./measure.ts";

const FONT = "13px system-ui, sans-serif";
const NODE_R = 5;
const DOT_R = 3.5;

/**
 * `show_map` (ADR-0056): the map as a tree that unlocks with the course. The topic is the root, each
 * stage a node on the trunk, each section a leaf on its stage's branch. What `map_view.py` wrote
 * decides how a node is lit: locked until taught, hollow while taught, solid once through, a dot
 * where a check left a weak point. Read-only; nothing here posts back.
 */
export function Tree({ sessionId, args }: ComponentProps): React.ReactNode {
	const source = typeof args.source === "string" ? args.source : undefined;
	const { data, error, loading } = useWorkspaceJson<unknown>(sessionId, source);
	const [box, width] = useElementWidth(560);
	const measure = useMemo(() => canvasMeasurer(FONT, measureText), []);

	if (!source) return <div className="component error">tree 组件缺少 source 参数，收到的参数：{JSON.stringify(args)}</div>;
	if (loading) return <div className="component tree-component" ref={box} aria-hidden="true" />;
	if (error) return <div className="component error">{error}</div>;

	const parsed = parseTree(data);
	if (!parsed.ok) return <div className="component error">{source} {parsed.error}</div>;

	return (
		<div className="component tree-component" ref={box}>
			<TreeSvg tree={parsed.tree} width={width} measure={measure} />
		</div>
	);
}

/** The drawing alone, given the parsed view and the width it has to fit. */
export function TreeSvg({ tree, width, measure }: { tree: TreeView; width: number; measure?: (t: string) => number }) {
	const layout = layoutTree(tree, width, measure);
	return (
		<svg width={layout.width} height={layout.height} viewBox={`0 0 ${layout.width} ${layout.height}`} role="img" aria-label={`地图：${layout.root.label}`}>
			<line className="tree-line" x1={layout.trunk.x} y1={layout.trunk.y1} x2={layout.trunk.x} y2={layout.trunk.y2} />
			<g className="tree-root">
				<circle cx={layout.root.x} cy={layout.root.y} r={NODE_R + 1} />
				<text x={layout.root.x + 14} y={layout.root.y + 4.5}>{layout.root.label}</text>
				{layout.root.title && <title>{layout.root.title}</title>}
			</g>
			{layout.stages.map((stage) => (
				<StageNode key={stage.id} stage={stage} />
			))}
		</svg>
	);
}

function StageNode({ stage }: { stage: StageBox }) {
	const { branch } = stage;
	return (
		<g className={`tree-stage ${stage.state}`}>
			{branch.y2 > branch.y1 && (
				<path className="tree-line" d={`M ${stage.x} ${stage.y} H ${branch.x} V ${branch.y2}`} />
			)}
			<circle cx={stage.x} cy={stage.y} r={NODE_R} />
			<text x={stage.x + 14} y={stage.y + 4.5}>
				{stage.state === "locked" ? `${stage.label} 🔒` : stage.label}
			</text>
			{stage.title && <title>{stage.title}</title>}
			{stage.leaves.map((leaf) => (
				<LeafNode key={leaf.id} leaf={leaf} branchX={branch.x} />
			))}
		</g>
	);
}

/** A section's pill, or -- smaller, on the row under its section -- one of its concepts. */
function LeafNode({ leaf, branchX }: { leaf: Leaf; branchX: number }) {
	const cy = leaf.y;
	const twig = leaf.kind === "concept";
	const dot = twig ? DOT_R - 1 : DOT_R;
	return (
		<g className={`tree-leaf ${leaf.state}${twig ? " tree-twig" : ""}`}>
			{leaf.first && <line className="tree-line" x1={branchX} y1={cy} x2={leaf.x} y2={cy} />}
			<rect x={leaf.x} y={cy - leaf.h / 2} width={leaf.w} height={leaf.h} rx={leaf.h / 2} />
			<circle className="tree-mark" cx={leaf.x + (twig ? 9 : 11)} cy={cy} r={dot} />
			<text x={leaf.x + (twig ? 16 : 19)} y={cy + (twig ? 3.5 : 4.5)}>{leaf.label}</text>
			{leaf.weak && <circle className="tree-weak" cx={leaf.x + leaf.w - 1} cy={cy - leaf.h / 2 + 1} r={3.5} />}
			<title>{leaf.title}</title>
		</g>
	);
}
